import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { FaLock, FaPlay } from "react-icons/fa";
import { IoClose } from "react-icons/io5";
import { toast } from "react-toastify";
import Leftsidebar from "../sidebar/leftsidebar";
import LeftResponsivesidebar from "../sidebar/left-responsive";
import Headmenu from "../sidebar/headmenu";
import profileimg from "../../assets/profile.png";
import useIvsPlayer from "../../hook/useIvsPlayer";
import { formatDateOnly } from "../../utils/date";
import { getReplay, listReplays } from "../services/subscriptionService";

const PAGE_SIZE = 20;

const hostName = (host) =>
  host?.account_type === "business" ? host.business_name : [host?.first_name, host?.last_name].filter(Boolean).join(" ");

const formatDuration = (seconds) => {
  const minutes = Math.round((Number(seconds) || 0) / 60);
  return minutes >= 60 ? `${Math.floor(minutes / 60)}h ${minutes % 60}m` : `${minutes}m`;
};

const ReplayPlayer = ({ replay, onClose }) => {
  const videoRef = useRef(null);
  useIvsPlayer(videoRef, replay.replay_url);

  return (
    <div className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/80 px-4" onClick={onClose}>
      <div className="w-full max-w-4xl" onClick={(e) => e.stopPropagation()}>
        <div className="mb-2 flex items-center justify-between text-white">
          <p className="truncate font-semibold">{replay.title || "Past live"}</p>
          <button type="button" onClick={onClose} aria-label="Close">
            <IoClose className="text-2xl" />
          </button>
        </div>
        <video ref={videoRef} controls playsInline className="aspect-video w-full rounded-xl bg-black" />
      </div>
    </div>
  );
};

const PastLives = () => {
  const [replays, setReplays] = useState([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [playing, setPlaying] = useState(null);
  const [openingId, setOpeningId] = useState(null);

  const loadPage = useCallback((pageToLoad) => {
    setLoading(true);
    listReplays({ page: pageToLoad, limit: PAGE_SIZE })
      .then((result) => {
        setReplays((prev) => (pageToLoad === 1 ? result.replays : [...prev, ...result.replays]));
        setHasMore(result.has_more);
        setPage(pageToLoad);
      })
      .catch((error) => toast.error(error.message))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    loadPage(1);
  }, [loadPage]);

  const openReplay = async (replay) => {
    setOpeningId(replay.stream_id);
    try {
      setPlaying(await getReplay(replay.stream_id));
    } catch (error) {
      toast.error(error.message);
    } finally {
      setOpeningId(null);
    }
  };

  return (
    <div className="dashboard w-full min-h-screen block lg:grid lg:grid-cols-[auto_minmax(0,1fr)]">
      <div className="h-full">
        <Leftsidebar />
      </div>

      <main className="flex-1 min-w-0 overflow-x-hidden p-4 md:p-6 md:pl-8">
        <LeftResponsivesidebar />
        <Headmenu />

        <div className="mt-4 space-y-6">
          <h1 className="text-2xl font-bold text-gray-900">Past Lives</h1>

          {!loading && !replays.length && <p className="text-sm text-gray-500">No past lives to watch yet.</p>}

          <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
            {replays.map((replay) => (
              <div key={replay.stream_id} className="flex flex-col rounded-2xl bg-[#F4F4F4] p-4">
                <div className="flex items-center gap-3">
                  <img
                    src={replay.host?.profile_picture_url || profileimg}
                    alt=""
                    className="h-10 w-10 rounded-full object-cover"
                  />
                  <div className="min-w-0">
                    <p className="truncate text-sm font-semibold text-gray-900">{hostName(replay.host)}</p>
                    <p className="text-xs text-gray-500">
                      {formatDateOnly(replay.started_at)} · {formatDuration(replay.duration)}
                    </p>
                  </div>
                </div>
                <p className="mt-3 line-clamp-2 flex-1 text-sm text-gray-800">{replay.title || "Live session"}</p>
                {replay.can_watch ? (
                  <button
                    type="button"
                    disabled={openingId === replay.stream_id}
                    onClick={() => openReplay(replay)}
                    className="mt-4 flex items-center justify-center gap-2 rounded-full bg-gradient-to-r from-[#08AEEA] to-[#1DE78B] px-4 py-2 text-sm font-semibold text-white disabled:opacity-60"
                  >
                    <FaPlay className="text-xs" />
                    {openingId === replay.stream_id ? "Opening..." : "Watch Replay"}
                  </button>
                ) : (
                  <Link
                    to="/subscription?tab=viewer"
                    className="mt-4 flex items-center justify-center gap-2 rounded-full border border-[#095228] px-4 py-2 text-sm font-semibold text-[#095228]"
                  >
                    <FaLock className="text-xs" />
                    Viewer Premium
                  </Link>
                )}
              </div>
            ))}
          </div>

          {hasMore && (
            <button
              type="button"
              disabled={loading}
              onClick={() => loadPage(page + 1)}
              className="mx-auto block rounded-full border border-gray-300 px-6 py-2 text-sm font-semibold text-gray-700 disabled:opacity-60"
            >
              {loading ? "Loading..." : "Load more"}
            </button>
          )}
        </div>
      </main>

      {playing && <ReplayPlayer replay={playing} onClose={() => setPlaying(null)} />}
    </div>
  );
};

export default PastLives;
